import {
  apiDeletePushSubscription,
  apiGetPushPublicKey,
  apiSavePushSubscription,
} from './api';

const pushSupportIssue = () => {
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
  if (isIos && !isStandalone) return 'install_required';
  if (!window.isSecureContext) return 'insecure';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  return null;
};

export const supportsPushNotifications = () => !pushSupportIssue();

const applicationServerKey = (value) => {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
};

const getRegistration = async () => {
  await navigator.serviceWorker.register('/push-sw.js');
  return navigator.serviceWorker.ready;
};

export const getPushNotificationState = async () => {
  const supportIssue = pushSupportIssue();
  if (supportIssue) return supportIssue;
  if (Notification.permission === 'denied') return 'blocked';
  if (Notification.permission !== 'granted') return 'disabled';

  const registration = await getRegistration();
  const subscription = await registration.pushManager.getSubscription();
  return subscription ? 'enabled' : 'disabled';
};

export const enablePushNotifications = async (token) => {
  const supportIssue = pushSupportIssue();
  if (supportIssue === 'install_required') throw new Error('On iPhone or iPad, first add this portal to your Home Screen, then enable notifications from the installed app.');
  if (supportIssue === 'insecure') throw new Error('Browser notifications require a secure HTTPS connection.');
  if (supportIssue) throw new Error('This browser does not support push notifications.');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? 'Notifications are blocked. Allow them in your browser or phone settings.'
      : 'Notification permission was not granted.');
  }

  const registration = await getRegistration();
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    const { publicKey } = await apiGetPushPublicKey(token);
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(publicKey),
    });
  }

  await apiSavePushSubscription(token, subscription.toJSON());
  await registration.showNotification('Browser notifications enabled', {
    body: 'New Evercrest activity can now reach this device while the portal is closed.',
    icon: '/favicon.png',
    badge: '/favicon.png',
    tag: 'push-enabled',
  });
  return 'enabled';
};

export const disablePushNotifications = async (token) => {
  if (!supportsPushNotifications()) return 'unsupported';
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return 'disabled';

  try {
    await apiDeletePushSubscription(token, subscription.endpoint);
  } finally {
    await subscription.unsubscribe();
  }
  return 'disabled';
};

export const syncPushSubscription = async (token) => {
  if (!token || !supportsPushNotifications() || Notification.permission !== 'granted') {
    return getPushNotificationState();
  }

  const registration = await getRegistration();
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return 'disabled';
  await apiSavePushSubscription(token, subscription.toJSON());
  return 'enabled';
};
